import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical } from '@fortawesome/free-solid-svg-icons';
import './SmartQuiz.css';

const optionTypeLabel: Record<string, string> = {
  single: 'Multiple Choice',
  multiple: 'Multiple Choice',
  trueorfalse: 'True / False',
  freetext: 'Short Answer',
};

const optionTypeBadgeClass: Record<string, string> = {
  single: 'sq-q-badge--mc',
  multiple: 'sq-q-badge--mc',
  trueorfalse: 'sq-q-badge--tf',
  freetext: 'sq-q-badge--sa',
};

const letterLabels = ['A', 'B', 'C', 'D', 'E', 'F'];

interface Props {
  questions: CreateQuestionItem[];
  selectedIdx: number;
  onSelectIdx: (i: number) => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  onLoadMore?: () => void;
  isLastPage?: boolean;
  loadingMore?: boolean;
}

const StepQuestions: React.FC<Props> = ({
  questions,
  selectedIdx,
  onSelectIdx,
  onSaveDraft,
  onPublish,
  onLoadMore,
  isLastPage = true,
  loadingMore = false,
}) => {
  const selected = questions[selectedIdx];

  return (
    <div className="sq-step3">
      {/* Left: question list */}
      <div className="sq-step3__list">
        {questions.map((q, i) => (
          <button
            key={q.questionId}
            className={`sq-step3__item ${i === selectedIdx ? 'sq-step3__item--active' : ''}`}
            onClick={() => onSelectIdx(i)}
          >
            <FontAwesomeIcon icon={faGripVertical} className="sq-step3__drag" />
            <span className="sq-step3__num">{i + 1}</span>
            <div className="sq-step3__item-body">
              <span className="sq-step3__item-text">{q.questionMessage}</span>
              <span className={`sq-q-badge ${optionTypeBadgeClass[q.optionType] || 'sq-q-badge--mc'}`}>
                {optionTypeLabel[q.optionType] || q.optionType}
              </span>
            </div>
          </button>
        ))}
        {!isLastPage && (
          <button
            className="sq-btn sq-btn--outline sq-btn--full"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load More Questions'}
          </button>
        )}
      </div>

      {/* Right: question detail */}
      <div className="sq-step3__detail">
        {selected && (
          <div className="sq-step3__detail-card">
            <span className={`sq-q-badge ${optionTypeBadgeClass[selected.optionType] || 'sq-q-badge--mc'}`}>
              {optionTypeLabel[selected.optionType] || selected.optionType}
            </span>
            <h3 className="sq-step3__question-text">{selected.questionMessage}</h3>

            <p className="sq-label">Answer Options</p>
            <div className="sq-step3__options-grid">
              {selected.options?.map((opt, i) => {
                const isCorrect = selected.answers?.includes(opt.answerText);
                return (
                  <div
                    key={i}
                    className={`sq-step3__option ${isCorrect ? 'sq-step3__option--correct' : ''}`}
                  >
                    <span className={`sq-step3__option-letter ${isCorrect ? 'sq-step3__option-letter--correct' : ''}`}>
                      {letterLabels[i] || i + 1}
                    </span>
                    <span className="sq-step3__option-text">{opt.answerText}</span>
                    {isCorrect && <span className="sq-step3__correct-tag">Correct</span>}
                  </div>
                );
              })}

              {/* Freetext answers fallback */}
              {(!selected.options || selected.options.length === 0) &&
                selected.answers?.map((a, i) => (
                  <div key={i} className="sq-step3__option sq-step3__option--correct">
                    <span className="sq-step3__option-letter sq-step3__option-letter--correct">
                      {letterLabels[i] || i + 1}
                    </span>
                    <span className="sq-step3__option-text">{a}</span>
                    <span className="sq-step3__correct-tag">Answer</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Bottom bar */}
        <div className="sq-step3__bottom">
          <span className="sq-step3__ready">{questions.length} questions ready</span>
          <div className="sq-step3__bottom-actions">
            <button className="sq-btn sq-btn--outline" onClick={onSaveDraft}>Save as Draft</button>
            <button className="sq-btn sq-btn--primary" onClick={onPublish}>Publish</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepQuestions;
